import { Button } from "@/components/ui/button";
import { Phone, Mail, ArrowLeft, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePlatform } from "@/hooks/usePlatform";
import Logo from "@/components/Logo";

interface Tier {
  id: 'regular' | 'enterprise';
  tag: string;
  name: string;
  monthlyNaira: string;
  annualNaira: string;
  annualFull: string;
  annualSaving: string;
  blurb: string;
  included: string[];
  highlight?: boolean;
  cta: string;
  ctaStyle: 'primary' | 'ghost';
}

const tiers: Tier[] = [
  {
    id: 'regular',
    tag: 'TIER 01',
    name: 'Regular',
    monthlyNaira: '₦5,000',
    annualNaira: '₦50,000',
    annualFull: '₦60,000',
    annualSaving: 'Save ₦10,000',
    blurb: 'Everything to run a karaoke night — singer queue, song library, and live display. Perfect for a single venue.',
    included: [
      'Live queue management',
      'Song library (CSV upload)',
      'Singer request system',
      'Custom song requests',
      'Queue display screen for projector',
      'Basic venue branding',
      'Mobile-responsive singer page',
      'Real-time updates across devices',
    ],
    cta: 'Email for setup',
    ctaStyle: 'ghost',
  },
  {
    id: 'enterprise',
    tag: 'TIER 02',
    name: 'Enterprise',
    monthlyNaira: '₦10,000',
    annualNaira: '₦100,000',
    annualFull: '₦120,000',
    annualSaving: 'Save ₦20,000',
    blurb: 'For venues that hand off the night to a dedicated host and want full control over the show.',
    highlight: true,
    included: [
      'Everything in Regular, plus:',
      'Show Host account (one per venue)',
      'Advanced Show Mode',
      'Announcements with priority & expiry',
      'Custom song approvals queue',
      'Excel export of past requests',
      'Advanced branding & customisation',
      'Priority support',
    ],
    cta: 'Email for setup',
    ctaStyle: 'primary',
  },
];

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { isNative } = usePlatform();

  // Option A: only existing venue admins can pay. Others are sent to sign in;
  // brand-new venues still onboard via the contact section below.
  const isVenueAdmin =
    typeof window !== "undefined" &&
    localStorage.getItem("hostAuth") === "true" &&
    localStorage.getItem("accountType") === "venue_admin";

  const ctaLabel = isNative
    ? "Email for setup"
    : isVenueAdmin
    ? "Subscribe / Renew"
    : "Sign in to subscribe";

  const handleSelectPlan = (t: Tier) => {
    // Native apps can't take Paystack payments (App Store / Play Store rules).
    if (isNative) {
      window.location.href = `mailto:tubosun@nairoke.com?subject=KaraokeCore ${t.name} Plan Setup`;
      return;
    }
    if (isVenueAdmin) {
      navigate(`/host-dashboard?checkout=${t.id}&currency=NGN`);
      return;
    }
    navigate("/venue");
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-50 border-b border-console-line bg-console-bg/85 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="relative shrink-0">
                <Logo tone="text" size="md" />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-console-ok shadow-[0_0_6px_#3DDC97]" />
              </div>
              <div className="flex flex-col leading-none min-w-0">
                <span className="font-geist font-semibold text-[14px] sm:text-[15px] tracking-tight text-console-text truncate">KaraokeCore</span>
                <span className="font-geist-mono text-[9px] sm:text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1">Plans & pricing</span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                onClick={() => navigate('/international')}
                className="hidden sm:flex h-9 px-3 text-[12px] font-medium font-geist text-console-muted hover:bg-white/5 hover:text-console-text border border-console-line rounded-[6px]"
              >
                <Globe className="h-3.5 w-3.5 mr-1.5" />
                Global rates
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
            </div>
          </div>
        </header>

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-10 sm:pb-12">
          <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-signal">
            04 / Plans & pricing
          </span>
          <h1 className="font-geist font-semibold text-[clamp(32px,6vw,64px)] leading-[1.05] tracking-[-0.03em] mt-4 sm:mt-5 max-w-3xl">
            One price per venue.<br />
            <span className="text-console-muted">Billed monthly in naira.</span>
          </h1>
          <p className="font-geist text-[15px] sm:text-[16px] leading-relaxed text-console-muted max-w-xl mt-4 sm:mt-6">
            No per-singer charges. No song-store add-ons. Local naira rates — subsidised against our global USD pricing. A one-time setup fee of{' '}
            <span className="text-console-text font-medium">₦50,000</span> covers onboarding, catalogue import, and getting your venue live.
          </p>
          <button
            onClick={() => navigate('/international')}
            className="mt-4 inline-flex items-center gap-2 min-h-[44px] px-1 font-geist-mono text-[11px] text-console-muted hover:text-console-signal transition-colors"
          >
            <Globe className="h-3.5 w-3.5 shrink-0" />
            Outside Nigeria? View global USD rates →
          </button>
        </section>

        {/* ── Setup fee banner ────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mb-5 sm:mb-6">
          <div className="max-w-5xl border border-console-line rounded-[8px] bg-console-surface px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-geist-mono text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-console-signal shrink-0">ONE-TIME</span>
              <span className="font-geist font-semibold text-[16px] sm:text-[18px] tracking-[-0.01em]">Setup Fee — ₦50,000</span>
            </div>
            <p className="font-geist text-[13px] text-console-muted leading-snug sm:max-w-sm">
              Covers account provisioning, song catalogue import, projector screen setup, and your first live walkthrough. Applies to both tiers.
            </p>
          </div>
        </section>

        {/* ── Tier cards ──────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
            {tiers.map((t) => (
              <div
                key={t.id}
                className={`bg-console-surface border rounded-[12px] p-5 sm:p-7 lg:p-8 relative flex flex-col gap-4 sm:gap-5 ${
                  t.highlight ? 'border-console-primary/40 bg-console-surface-2' : 'border-console-line'
                }`}
              >
                {t.highlight && (
                  <span className="absolute -top-2.5 left-5 sm:left-6 font-geist-mono text-[10px] tracking-[0.16em] bg-console-primary text-console-bg px-2 py-1 rounded-[3px]">
                    MOST VENUES PICK THIS
                  </span>
                )}

                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-muted mb-2 sm:mb-3">
                      {t.tag}
                    </div>
                    <div className="font-geist font-semibold text-[28px] sm:text-[36px] leading-none tracking-[-0.02em]">
                      {t.name}
                    </div>
                  </div>
                  <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${t.highlight ? 'bg-console-primary shadow-[0_0_8px_#4D7CFE]' : 'bg-console-line-strong'}`} />
                </div>

                {/* ── Price block ── */}
                <div className="pb-4 sm:pb-5 border-b border-console-line space-y-2.5 sm:space-y-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-geist font-semibold text-[32px] sm:text-[38px] leading-none tracking-[-0.02em]">
                      {t.monthlyNaira}
                    </span>
                    <span className="font-geist-mono text-[11px] sm:text-[12px] text-console-muted">/ month per venue</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-geist font-semibold text-[14px] sm:text-[15px]">{t.annualNaira}</span>
                    <span className="font-geist-mono text-[11px] text-console-muted line-through">{t.annualFull}</span>
                    <span className="font-geist-mono text-[10px] tracking-[0.12em] uppercase text-console-signal">{t.annualSaving}</span>
                    <span className="font-geist-mono text-[11px] text-console-muted">/ year</span>
                  </div>

                </div>

                <p className="font-geist text-[13px] sm:text-[14px] leading-relaxed text-console-muted">{t.blurb}</p>

                <ul className="flex flex-col gap-2.5 sm:gap-3 flex-1">
                  {t.included.map((feature, i) => (
                    <li key={i} className="font-geist text-[13px] sm:text-[14px] leading-snug text-console-text flex gap-2.5 items-start">
                      <span className="font-geist-mono text-[12px] leading-relaxed text-console-signal flex-none mt-px">+</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(t)}
                  className={`min-h-[44px] px-5 text-[14px] font-semibold font-geist rounded-[6px] w-full sm:w-auto ${
                    t.ctaStyle === 'primary'
                      ? 'bg-console-primary text-console-bg hover:bg-console-primary/90'
                      : 'bg-transparent text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong'
                  }`}
                  variant={t.ctaStyle === 'primary' ? 'default' : 'ghost'}
                >
                  {ctaLabel}
                  <span className="font-geist-mono text-[11px] opacity-70 ml-2">→</span>
                </Button>
              </div>
            ))}
          </div>

          <p className="font-geist-mono text-[10px] sm:text-[11px] leading-relaxed text-console-dim mt-8 sm:mt-10 max-w-3xl">
            FOOTNOTES — Pricing is per active venue. Pay monthly; annual commitment unlocks a discounted yearly total (Regular ₦50,000/yr vs ₦60,000 full-rate; Enterprise ₦100,000/yr vs ₦120,000 full-rate). A one-time ₦50,000 setup fee applies on first activation. Global USD rates ($5/$10 per month) are available for international venues — see <button onClick={() => navigate('/international')} className="underline underline-offset-2 text-console-muted hover:text-console-text transition-colors">/international</button>. Singers join for free. Bring your own song catalogue (CSV) at no extra charge. Demo and sandbox available before billing.
          </p>
        </section>

        {/* ── Contact ─────────────────────────────────────────────────── */}
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20 lg:mt-24 pb-16">
          <div className="bg-console-surface border border-console-line rounded-[10px] p-6 sm:p-8 lg:p-12">
            <span className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-signal">
              05 / Get in touch
            </span>
            <h2 className="font-geist font-semibold text-[clamp(22px,4vw,36px)] leading-[1.05] tracking-[-0.025em] mt-3 sm:mt-4 max-w-2xl">
              Talk to us about your floor.
            </h2>
            <p className="font-geist text-[14px] sm:text-[15px] text-console-muted mt-3 sm:mt-4 max-w-xl">
              We'll confirm pricing for your venue, help you import your song catalogue, and get you on air.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-7">
              <Button
                onClick={() => window.location.href = 'mailto:tubosun@nairoke.com?subject=KaraokeCore Subscription Inquiry'}
                className="min-h-[44px] px-5 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
              >
                <Mail className="h-4 w-4 mr-2" />
                tubosun@nairoke.com
              </Button>
              <Button
                variant="ghost"
                onClick={() => window.location.href = 'tel:+2348109385214'}
                className="min-h-[44px] px-5 text-[14px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
              >
                <Phone className="h-4 w-4 mr-2" />
                +234 810 938 5214
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SubscriptionPage;
